import type { AccessDeviceManagementAttempt } from '@/api/core/access-device-management';
import { putAccessDeviceManagement } from '@/api/core/access-device-management';
import type { AccessDeviceRegistryItem } from '@/api/core/access-device-registry';
import { canWriteProperties, coreQueryKey } from '@/api/core/authorization';
import {
  readNativeCatalog,
  type NativeCatalogUnit,
  type NativeProperty,
} from '@/api/core/native-property-read';
import { isApiError } from '@/api/http/errors';
import { useOperatorSession } from '@/auth/runtime';
import { UnsavedGuard } from '@/components/unsaved-guard';
import {
  Alert,
  AlertDescription,
  Button,
  ConfirmDialog,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mgrvtechgroup/admin-ds';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useInsertionEffect, useMemo, useRef, useState } from 'react';
import {
  accessDeviceCommandContextQuery,
  nativePropertiesQuery,
  type PropertyRuntime,
} from '../property/property-queries';

interface FrozenAttempt extends AccessDeviceManagementAttempt {
  readonly apiNamespace: string;
  readonly generation: number;
}

function connectionLabel(device: AccessDeviceRegistryItem, slotId: string): string {
  const connection = device.connections.find(
    (item) => item.slot_id.toLowerCase() === slotId.toLowerCase(),
  );
  if (!connection) return `공간 확인 필요 · 슬롯 UUID ${slotId}`;
  if (connection.resolution_state !== 'RESOLVED') {
    const state =
      connection.resolution_state === 'DELETED'
        ? '삭제된 공간'
        : connection.resolution_state === 'MISSING'
          ? '공간 정보 미존재'
          : '공간 확인 필요';
    return `${state} · 슬롯 UUID ${slotId}`;
  }
  const roomLabel = connection.room_no ?? '번호 미등록';
  return `${connection.property_name ?? '지점 이름 미확인'} · 호실 ${roomLabel} · 슬롯 ${connection.slot_no === null ? '번호 미등록' : connection.slot_no === '' ? '빈 값' : connection.slot_no}`;
}

function slotLabel(
  slot: NativeCatalogUnit,
  roomNo: string | undefined,
  property: NativeProperty | undefined,
): string {
  const roomLabel = roomNo ?? '정보 없음';
  return `${property ? `${property.name} · ${property.code}` : '선택 지점'} · 호실 ${roomLabel} · 슬롯 ${slot.name === '' ? '빈 값' : slot.name}`;
}

function deviceLabel(device: AccessDeviceRegistryItem): string {
  return device.device_name?.length ? device.device_name : device.device_id;
}

function messageFor(error: unknown): string {
  if (isApiError(error)) {
    if (error.status === 400) return '연결 변경 내용을 확인해 주세요. 초안은 보존했습니다.';
    if (error.status === 403) return '현재 연결 변경 권한을 확인할 수 없습니다.';
    if (error.status === 404) return '선택한 공간이나 변경 범위를 확인하지 못했습니다.';
    if (error.status === 409)
      return '저장 정보가 바뀌었습니다. 최신 연결을 확인하고 초안을 다시 검토해 주세요.';
    if (
      error.kind === 'network' ||
      error.kind === 'timeout' ||
      error.kind === 'invalid-response' ||
      (error.status !== undefined && error.status >= 500)
    )
      return '저장 결과를 확인하지 못했습니다. 같은 변경을 자동으로 다시 보내지 않았습니다.';
  }
  return '연결 변경 결과를 확인하지 못했습니다. 초안은 보존했습니다.';
}

export function AccessDeviceSlotLinksForm({
  runtime,
  generation,
  device,
  onClose,
  onSaved,
  refreshRegistry,
}: {
  runtime: PropertyRuntime;
  generation: number;
  device: AccessDeviceRegistryItem;
  onClose: () => void;
  onSaved: (message: string) => void;
  refreshRegistry: () => Promise<AccessDeviceRegistryItem | undefined>;
}) {
  const session = useOperatorSession(runtime.session);
  const queryClient = useQueryClient();
  const originalDevice = useRef(device).current;
  const [propertyCursor, setPropertyCursor] = useState<string | null>(null);
  const [roomCursors, setRoomCursors] = useState<(string | null)[]>([null]);
  const roomCursor = roomCursors.at(-1) ?? null;
  const [catalogRead, setCatalogRead] = useState(0);
  const [propertyId, setPropertyId] = useState('');
  const [search, setSearch] = useState('');
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [desired, setDesired] = useState(() => [...originalDevice.slot_ids]);
  const [catalogPage, setCatalogPage] = useState<{
    cursor: string | null;
    nextCursor: string | null;
    propertyId: string;
    slots: NativeCatalogUnit[];
    roomNos: Map<string, string>;
    property: NativeProperty;
  } | null>(null);
  const [candidateProofs, setCandidateProofs] = useState<
    Map<string, { slot: NativeCatalogUnit; roomNo: string; property: NativeProperty }>
  >(() => new Map());
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [catalogError, setCatalogError] = useState<string>();
  const [reviewed, setReviewed] = useState(false);
  const [attempt, setAttempt] = useState<FrozenAttempt | null>(null);
  const [pending, setPending] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState<string>();
  const [receipt, setReceipt] = useState<string>();
  const [latest, setLatest] = useState<AccessDeviceRegistryItem>();
  const [reconcileRequired, setReconcileRequired] = useState(false);
  const [acknowledgedRevision, setAcknowledgedRevision] = useState<number>();
  const [acknowledgeLatest, setAcknowledgeLatest] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const flight = useRef<object | null>(null);
  const controller = useRef<AbortController | null>(null);
  const activeScope = useRef({
    apiNamespace: runtime.apiNamespace,
    generation,
    deviceId: originalDevice.core_device_id,
    propertyId,
  });
  useInsertionEffect(() => {
    activeScope.current = {
      apiNamespace: runtime.apiNamespace,
      generation,
      deviceId: originalDevice.core_device_id,
      propertyId,
    };
  }, [runtime.apiNamespace, generation, originalDevice.core_device_id, propertyId]);
  const canWrite =
    session.status === 'authorized' &&
    session.generation === generation &&
    !session.deniedDomains.includes('property') &&
    Boolean(session.authorization && canWriteProperties(session.authorization));
  const properties = useQuery(nativePropertiesQuery(runtime, generation, propertyCursor, canWrite));
  const candidateContext = useQuery(
    accessDeviceCommandContextQuery(
      runtime,
      generation,
      originalDevice.core_device_id,
      null,
      canWrite,
    ),
  );
  const context = useQuery(
    accessDeviceCommandContextQuery(
      runtime,
      generation,
      originalDevice.core_device_id,
      propertyId || null,
      canWrite && Boolean(propertyId),
      () => {
        const current = runtime.session.getSnapshot();
        return (
          current.status === 'authorized' &&
          current.generation === generation &&
          activeScope.current.apiNamespace === runtime.apiNamespace &&
          activeScope.current.deviceId === originalDevice.core_device_id &&
          activeScope.current.propertyId === propertyId
        );
      },
    ),
  );
  const currentScope = useCallback(() => {
    const current = runtime.session.getSnapshot();
    return (
      current.status === 'authorized' &&
      current.generation === generation &&
      Boolean(current.authorization && canWriteProperties(current.authorization)) &&
      activeScope.current.apiNamespace === runtime.apiNamespace &&
      activeScope.current.generation === generation &&
      activeScope.current.deviceId === originalDevice.core_device_id &&
      activeScope.current.propertyId === propertyId
    );
  }, [runtime, generation, originalDevice.core_device_id, propertyId]);
  const propertyOptions = useMemo(() => {
    const permitted = candidateContext.data?.requires_property_selection
      ? (properties.data?.data.map((item) => item.id) ?? [])
      : (candidateContext.data?.command_property_ids ?? []);
    return permitted.flatMap((id) => {
      const property = properties.data?.data.find(
        (item) => item.id.toLowerCase() === id.toLowerCase(),
      );
      return property ? [property] : [];
    });
  }, [candidateContext.data, properties.data]);
  const selectedProperty = properties.data?.data.find(
    (item) => item.id.toLowerCase() === propertyId.toLowerCase(),
  );
  const selectedContext = context.data;
  const candidateAllowed = Boolean(
    candidateContext.data &&
      (candidateContext.data.requires_property_selection
        ? selectedProperty !== undefined
        : candidateContext.data.command_property_ids.includes(propertyId)),
  );
  const validScope = Boolean(
    propertyId &&
      candidateAllowed &&
      selectedContext?.command_property_ids.length === 1 &&
      selectedContext.command_property_ids[0] === propertyId &&
      !selectedContext.requires_property_selection,
  );

  useEffect(() => {
    if (!propertyId || !validScope) {
      setCatalogPage(null);
      setCandidateProofs(new Map());
      setCatalogError(undefined);
      return;
    }
    const abort = new AbortController();
    setCatalogError(undefined);
    setCatalogBusy(true);
    void (async () => {
      try {
        if (abort.signal.aborted || !currentScope()) return;
        const result = await readNativeCatalog(
          runtime.client,
          propertyId,
          roomCursor,
          abort.signal,
        );
        const page = result.data;
        if (page.property.id.toLowerCase() !== propertyId.toLowerCase())
          throw new Error('catalog scope changed');
        abort.signal.throwIfAborted();
        if (!currentScope()) return;
        const roomNos = new Map(page.rooms.map((room) => [room.id, room.name]));
        const liveSlots = page.rooms.flatMap((room) => room.units);
        setCatalogPage({
          cursor: roomCursor,
          nextCursor: result.next_cursor,
          propertyId,
          slots: liveSlots,
          roomNos,
          property: page.property,
        });
        setCandidateProofs((previous) => {
          const next = new Map(previous);
          for (const slot of liveSlots) {
            const roomNo = roomNos.get(slot.room_id);
            if (roomNo !== undefined)
              next.set(slot.id.toLowerCase(), { slot, roomNo, property: page.property });
          }
          return next;
        });
      } catch (failure) {
        if (abort.signal.aborted || !currentScope()) return;
        if (isApiError(failure) && failure.status === 403)
          runtime.session.reportDomainAccessDenied('property', generation);
        setCatalogError(
          '공간 후보 페이지를 확인하지 못했습니다. 다시 불러오거나 이전 페이지로 돌아가세요.',
        );
      } finally {
        if (!abort.signal.aborted && currentScope()) setCatalogBusy(false);
      }
    })();
    return () => {
      abort.abort();
    };
  }, [propertyId, validScope, roomCursor, catalogRead, runtime, generation, currentScope]);

  useEffect(() => () => controller.current?.abort(), []);
  useEffect(() => {
    if (!canWrite) controller.current?.abort();
  }, [canWrite]);
  const currentSlots = new Set(desired.map((id) => id.toLowerCase()));
  const added = desired.filter(
    (id) =>
      !originalDevice.slot_ids.some((original) => original.toLowerCase() === id.toLowerCase()),
  );
  const removed = originalDevice.slot_ids.filter(
    (id) => !desired.some((current) => current.toLowerCase() === id.toLowerCase()),
  );
  const catalogSlotsById = new Map([...candidateProofs].map(([id, proof]) => [id, proof.slot]));
  const searchableSlots = [...candidateProofs.values()]
    .filter((proof) => proof.property.id.toLowerCase() === propertyId.toLowerCase())
    .map((proof) => proof.slot)
    .filter((slot) => !currentSlots.has(slot.id.toLowerCase()))
    .filter((slot) => {
      const proof = candidateProofs.get(slot.id.toLowerCase());
      const text =
        `${slotLabel(slot, proof?.roomNo, proof?.property)} ${slot.id}`.toLocaleLowerCase();
      return text.includes(search.toLocaleLowerCase());
    });
  const dirty =
    !receipt &&
    (Boolean(propertyId) ||
      added.length > 0 ||
      removed.length > 0 ||
      uncertain ||
      attempt !== null);

  const close = () => {
    controller.current?.abort();
    onClose();
  };
  const reconcile = async () => {
    setError(undefined);
    const [freshDevice, freshContext] = await Promise.all([refreshRegistry(), context.refetch()]);
    if (!currentScope()) return;
    if (
      !freshDevice ||
      freshContext.isError ||
      !freshContext.data ||
      freshDevice.core_device_id !== originalDevice.core_device_id ||
      freshContext.data.revision !== freshDevice.revision
    ) {
      setError('최신 장비 연결과 변경 범위를 함께 확인하지 못했습니다. 다시 시도해 주세요.');
      return;
    }
    setLatest(freshDevice);
    setAcknowledgeLatest(false);
    setAcknowledgedRevision(undefined);
    setReconcileRequired(false);
  };
  const send = async (retry = false) => {
    if (flight.current || !canWrite || !validScope || !currentScope()) return;
    let captured = attempt;
    if (
      retry &&
      (captured?.apiNamespace !== runtime.apiNamespace || captured.generation !== generation)
    )
      return;
    const activeFlight = {};
    flight.current = activeFlight;
    setPending(true);
    const abort = new AbortController();
    controller.current = abort;
    const isCurrent = () => currentScope() && !abort.signal.aborted;
    try {
      if (!retry) {
        const currentDevice = await refreshRegistry();
        const freshContext = await context.refetch();
        if (!isCurrent()) return;
        if (
          !currentDevice ||
          freshContext.isError ||
          !freshContext.data ||
          currentDevice.core_device_id !== originalDevice.core_device_id ||
          currentDevice.retired ||
          freshContext.data.revision !== currentDevice.revision
        ) {
          setError('최신 장비와 변경 범위를 확인하지 못했습니다. 다시 조회해 주세요.');
          return;
        }
        if (
          freshContext.data.core_device_id !== originalDevice.core_device_id ||
          freshContext.data.command_property_ids.length !== 1 ||
          freshContext.data.command_property_ids[0] !== propertyId
        ) {
          setError('선택한 변경 범위가 최신 확인 결과와 다릅니다. 초안을 보존했습니다.');
          return;
        }
        if (currentDevice.revision !== originalDevice.revision) {
          setLatest(currentDevice);
          if (acknowledgedRevision !== currentDevice.revision || !acknowledgeLatest) {
            setAcknowledgeLatest(false);
            setError(
              '최신 연결 차이를 확인했습니다. 아래 비교 내용을 읽고 초안을 명시적으로 확인해 주세요.',
            );
            return;
          }
          const nonLinkChanged =
            currentDevice.device_id !== originalDevice.device_id ||
            currentDevice.gateway_id !== originalDevice.gateway_id ||
            currentDevice.device_type !== originalDevice.device_type ||
            currentDevice.device_name !== originalDevice.device_name ||
            currentDevice.gateway_name !== originalDevice.gateway_name ||
            currentDevice.battery !== originalDevice.battery ||
            currentDevice.is_active !== originalDevice.is_active;
          if (nonLinkChanged) {
            setError(
              '저장된 장비 정보도 바뀌었습니다. 초안을 안전하게 다시 맞출 수 없습니다. 닫고 최신 정보로 다시 열어 주세요.',
            );
            return;
          }
        }
        if (currentDevice.gateway_id === null) {
          setError('이 기기는 객실 연결을 변경할 수 없습니다.');
          return;
        }
        const body = Object.freeze({
          command_id: crypto.randomUUID(),
          expected_revision: currentDevice.revision,
          device_id: currentDevice.device_id,
          gateway_id: currentDevice.gateway_id,
          is_active: currentDevice.is_active,
          slot_ids: Object.freeze([...desired]),
          device_type: currentDevice.device_type,
          retired: false,
          device_name: currentDevice.device_name,
          gateway_name: currentDevice.gateway_name,
          battery: currentDevice.battery,
        });
        if (
          body.slot_ids.length > 10_000 ||
          new TextEncoder().encode(JSON.stringify(body)).byteLength > 8_192
        ) {
          setError(
            '이 연결 목록은 현재 한 번에 저장할 수 있는 범위를 넘습니다. 연결을 줄이지 않고는 저장할 수 없습니다.',
          );
          return;
        }
        captured = Object.freeze({
          apiNamespace: runtime.apiNamespace,
          generation,
          property_id: propertyId,
          core_device_id: originalDevice.core_device_id,
          body,
        });
        setAttempt(captured);
      }
      if (!captured) return;
      const attemptToSave = captured;
      if (!attemptToSave.core_device_id) throw new Error('missing Core device UUID');
      const saved = await putAccessDeviceManagement(
        runtime.client,
        attemptToSave.property_id,
        attemptToSave.core_device_id,
        attemptToSave.body,
        abort.signal,
      );
      const savedDevice = saved.device;
      const expectedRevision = attemptToSave.body.expected_revision ?? 0;
      const validRevision =
        saved.effect === 'CHANGED'
          ? saved.revision > expectedRevision
          : saved.revision === expectedRevision;
      if (
        !isCurrent() ||
        savedDevice.core_device_id !== originalDevice.core_device_id ||
        !validRevision ||
        savedDevice.retired ||
        savedDevice.slot_ids.length !== attemptToSave.body.slot_ids.length ||
        savedDevice.slot_ids.some(
          (id) =>
            !attemptToSave.body.slot_ids.some((slot) => slot.toLowerCase() === id.toLowerCase()),
        )
      )
        return;
      setAttempt(null);
      setUncertain(false);
      const savedReceipt = `${saved.effect === 'NO_CHANGE' ? '최신 연결 상태를 확인했습니다.' : '연결 변경을 저장했습니다.'} ${String(added.length)}개 추가, ${String(removed.length)}개 해제.`;
      setReceipt(savedReceipt);
      onSaved(savedReceipt);
      const oldProperties = new Set(
        originalDevice.connections.flatMap((item) => (item.property_id ? [item.property_id] : [])),
      );
      oldProperties.add(attemptToSave.property_id);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: coreQueryKey(
            attemptToSave.apiNamespace,
            attemptToSave.generation,
            'access-device-registry',
          ),
          refetchType: 'active',
        }),
        ...[...oldProperties].flatMap((id) =>
          ['core-access-device-inventory', 'native-access-inventory'].map((resource) =>
            queryClient.invalidateQueries({
              queryKey: coreQueryKey(
                attemptToSave.apiNamespace,
                attemptToSave.generation,
                resource,
                id,
              ),
              refetchType: 'active',
            }),
          ),
        ),
      ]);
    } catch (failure) {
      if (!isCurrent()) return;
      if (isApiError(failure) && failure.status === 403)
        runtime.session.reportDomainAccessDenied('property', generation);
      const conflict = isApiError(failure) && failure.status === 409;
      const uncertainFailure =
        isApiError(failure) &&
        (failure.kind === 'network' ||
          failure.kind === 'timeout' ||
          failure.kind === 'invalid-response' ||
          (failure.status !== undefined && failure.status >= 500));
      if (conflict) {
        setAttempt(null);
        setUncertain(false);
        setLatest(undefined);
        setAcknowledgedRevision(undefined);
        setAcknowledgeLatest(false);
        setReconcileRequired(true);
      } else if (uncertainFailure || (retry && isApiError(failure) && failure.status === 404)) {
        setAttempt(captured);
        setUncertain(true);
      } else {
        setAttempt(null);
        setUncertain(false);
      }
      setError(messageFor(failure));
    } finally {
      if (flight.current === activeFlight) {
        flight.current = null;
        setPending(false);
        if (controller.current === abort) controller.current = null;
      }
    }
  };

  return (
    <section aria-label="기존 장비 연결 관리" className="space-y-4 rounded-md border p-4 sm:p-5">
      <UnsavedGuard
        when={canWrite && (dirty || pending)}
        warning={{
          target: uncertain ? '결과를 확인하지 못한 연결 변경' : '저장하지 않은 연결 변경',
          action: '초안을 버리고 이동',
          consequence:
            '이동하면 입력한 연결 변경을 잃습니다. 요청 결과가 불확실하면 같은 요청을 다시 확인할 수 없습니다.',
        }}
      />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="title-sm">연결할 공간 추가 또는 해제</h2>
          <p className="body-sm text-muted-foreground mt-1">
            {deviceLabel(originalDevice)} · 연결된 공간의 현재 상태만 바꿉니다.
          </p>
        </div>
        <Button
          variant="outline"
          disabled={pending}
          onClick={() => {
            if (dirty || pending) setCloseConfirmOpen(true);
            else close();
          }}
        >
          닫기
        </Button>
      </div>
      <FormField label="변경 범위 지점" htmlFor="slot-link-property">
        <Select
          value={propertyId}
          disabled={pending || uncertain || Boolean(attempt) || Boolean(receipt)}
          onValueChange={(value) => {
            if (!value) return;
            setPropertyId(value);
            setRoomCursors([null]);
            setCatalogPage(null);
            setCandidateProofs(new Map());
            setCatalogError(undefined);
            setSearch('');
            setSelectedSlotId('');
            setReviewed(false);
            setError(undefined);
            setLatest(undefined);
            setAcknowledgeLatest(false);
          }}
        >
          <SelectTrigger aria-label="변경 범위 지점">
            <SelectValue placeholder="명령 가능한 지점을 선택해 주세요">
              {(value) =>
                typeof value === 'string'
                  ? (propertyOptions.find((item) => item.id === value)?.name ?? value)
                  : ''
              }
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {propertyOptions.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.name}
                {item.code ? ` · ${item.code}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {properties.data?.next_cursor && !propertyId && (
          <Button
            variant="outline"
            size="sm"
            disabled={properties.isFetching || pending || Boolean(attempt)}
            onClick={() => {
              setPropertyCursor(properties.data.next_cursor);
            }}
          >
            다음 지점 목록 불러오기
          </Button>
        )}
      </FormField>
      {properties.isPending && canWrite && <p role="status">지점 목록을 불러오고 있습니다.</p>}
      {properties.isError && <p role="alert">지점 목록을 불러오지 못했습니다.</p>}
      {candidateContext.isError && (
        <Alert variant="destructive">
          <AlertDescription>장비 변경 범위를 확인하지 못했습니다.</AlertDescription>
        </Alert>
      )}
      {propertyId && !validScope && (
        <p className="body-sm text-muted-foreground">
          선택한 지점과 장비의 현재 변경 범위를 확인하고 있습니다.
        </p>
      )}
      {validScope && (
        <>
          <div className="space-y-2">
            <h3 className="body-strong">현재 연결 ({desired.length})</h3>
            {desired.length ? (
              <ul className="space-y-2">
                {desired.map((slotId) => {
                  const original = originalDevice.slot_ids.some(
                    (id) => id.toLowerCase() === slotId.toLowerCase(),
                  );
                  const catalogSlot = catalogSlotsById.get(slotId.toLowerCase());
                  return (
                    <li
                      key={slotId}
                      className="flex flex-wrap items-center justify-between gap-2 rounded border p-3"
                    >
                      <span className="body-sm break-all">
                        {original
                          ? connectionLabel(originalDevice, slotId)
                          : candidateProofs.get(slotId.toLowerCase()) && catalogSlot
                            ? slotLabel(
                                catalogSlot,
                                candidateProofs.get(slotId.toLowerCase())?.roomNo,
                                candidateProofs.get(slotId.toLowerCase())?.property,
                              )
                            : `새 연결 · 슬롯 UUID ${slotId}`}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pending || uncertain || Boolean(attempt) || Boolean(receipt)}
                        onClick={() => {
                          setDesired((current) =>
                            current.filter((id) => id.toLowerCase() !== slotId.toLowerCase()),
                          );
                          setReviewed(false);
                        }}
                      >
                        해제
                      </Button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="body-sm text-muted-foreground">현재 연결된 공간이 없습니다.</p>
            )}
          </div>
          <div className="space-y-2 rounded border p-3">
            <h3 className="body-strong">새 공간 연결</h3>
            <label className="flex flex-col gap-1">
              <span className="caption">불러온 슬롯에서 찾기</span>
              <Input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
                placeholder="호실 또는 슬롯 검색"
                disabled={catalogBusy || pending || Boolean(attempt)}
              />
            </label>
            {catalogBusy && <p className="body-sm">현재 공간 페이지를 확인하고 있습니다.</p>}
            {catalogError && (
              <Alert variant="destructive">
                <AlertDescription>{catalogError}</AlertDescription>
              </Alert>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={catalogBusy || roomCursors.length < 2 || pending || Boolean(attempt)}
                onClick={() => {
                  setCatalogPage(null);
                  setCatalogError(undefined);
                  setRoomCursors((current) => current.slice(0, -1));
                }}
              >
                이전 공간 페이지
              </Button>
              <span className="caption">공간 페이지 {String(roomCursors.length)}</span>
              {catalogPage?.nextCursor && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={catalogBusy || pending || Boolean(attempt)}
                  onClick={() => {
                    if (roomCursors.includes(catalogPage.nextCursor)) {
                      setCatalogError(
                        '공간 페이지 커서가 반복되어 더 읽지 않았습니다. 이전 페이지로 돌아가세요.',
                      );
                      return;
                    }
                    setCatalogPage(null);
                    setCatalogError(undefined);
                    setRoomCursors((current) => [...current, catalogPage.nextCursor]);
                  }}
                >
                  다음 공간 페이지
                </Button>
              )}
              {catalogError && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={catalogBusy || pending || Boolean(attempt)}
                  onClick={() => {
                    setCatalogRead((current) => current + 1);
                  }}
                >
                  공간 후보 다시 불러오기
                </Button>
              )}
            </div>
            {candidateProofs.size > 0 && (
              <div className="flex flex-wrap gap-2">
                <Select
                  value={selectedSlotId}
                  onValueChange={(value) => {
                    setSelectedSlotId(value ?? '');
                  }}
                  disabled={pending || Boolean(attempt) || searchableSlots.length === 0}
                >
                  <SelectTrigger aria-label="추가할 슬롯">
                    <SelectValue placeholder="현재 슬롯을 선택해 주세요">
                      {(value) =>
                        typeof value === 'string'
                          ? (searchableSlots.find((slot) => slot.id === value)?.name ?? value)
                          : ''
                      }
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {searchableSlots.map((slot) => {
                      const proof = candidateProofs.get(slot.id.toLowerCase());
                      return (
                        <SelectItem key={slot.id} value={slot.id}>
                          {slotLabel(slot, proof?.roomNo, proof?.property)}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  disabled={
                    !selectedSlotId ||
                    pending ||
                    Boolean(attempt) ||
                    !candidateProofs.has(selectedSlotId.toLowerCase()) ||
                    !searchableSlots.some(
                      (slot) => slot.id.toLowerCase() === selectedSlotId.toLowerCase(),
                    )
                  }
                  onClick={() => {
                    setDesired((current) =>
                      current.some((id) => id.toLowerCase() === selectedSlotId.toLowerCase())
                        ? current
                        : [...current, selectedSlotId],
                    );
                    setSelectedSlotId('');
                    setReviewed(false);
                  }}
                >
                  연결 추가
                </Button>
              </div>
            )}
          </div>
        </>
      )}
      {(added.length > 0 || removed.length > 0) && (
        <div className="space-y-2 rounded border p-3">
          <h3 className="body-strong">변경 검토</h3>
          <p className="body-sm">
            {deviceLabel(originalDevice)}: {String(added.length)}개 추가, {String(removed.length)}개
            해제
          </p>
          {added.map((id) => (
            <p key={`a-${id}`} className="caption break-all">
              추가 · {id}
            </p>
          ))}
          {removed.map((id) => (
            <p key={`r-${id}`} className="caption break-all">
              해제 · {connectionLabel(originalDevice, id)}
            </p>
          ))}
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={reviewed}
              onChange={(event) => {
                setReviewed(event.target.checked);
              }}
              disabled={pending || Boolean(attempt)}
            />
            <span className="body-sm">위 연결 변경을 확인했습니다.</span>
          </label>
        </div>
      )}
      {latest && latest.revision !== originalDevice.revision && (
        <div className="space-y-2 rounded border p-3">
          <h3 className="body-strong">최신 저장 연결과 비교</h3>
          <p className="body-sm">
            장비: {deviceLabel(latest)} · 저장된 연결 {String(latest.slot_ids.length)}개 · 초안{' '}
            {desired.length}개
          </p>
          {latest.slot_ids
            .filter(
              (id) =>
                !originalDevice.slot_ids.some(
                  (original) => original.toLowerCase() === id.toLowerCase(),
                ),
            )
            .map((id) => (
              <p key={id} className="caption break-all">
                최신 정보에만 있음 · {connectionLabel(latest, id)}. 초안에 추가하지 않으면 저장 시
                해제됩니다.
              </p>
            ))}
          {originalDevice.slot_ids
            .filter(
              (id) =>
                !latest.slot_ids.some((current) => current.toLowerCase() === id.toLowerCase()),
            )
            .map((id) => (
              <p key={id} className="caption break-all">
                최신 정보에서 해제됨 · {connectionLabel(originalDevice, id)}
              </p>
            ))}
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={acknowledgeLatest}
              onChange={(event) => {
                setAcknowledgeLatest(event.target.checked);
                if (event.target.checked) setAcknowledgedRevision(latest.revision);
              }}
            />
            <span className="body-sm">
              최신 연결 차이를 확인했고, 이 초안의 전체 연결 목록으로 저장하겠습니다.
            </span>
          </label>
        </div>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {uncertain && (
        <p className="body-sm">
          같은 연결 변경만 한 번 더 확인할 수 있습니다. 초안을 수정할 수 없습니다.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          disabled={
            !canWrite ||
            !validScope ||
            added.some(
              (id) =>
                candidateProofs.get(id.toLowerCase())?.property.id.toLowerCase() !==
                propertyId.toLowerCase(),
            ) ||
            pending ||
            uncertain ||
            Boolean(attempt) ||
            Boolean(receipt) ||
            reconcileRequired ||
            added.length + removed.length === 0 ||
            !reviewed ||
            (latest !== undefined &&
              latest.revision !== originalDevice.revision &&
              (!acknowledgeLatest || acknowledgedRevision !== latest.revision))
          }
          onClick={() => void send()}
        >
          {pending ? '저장 중' : '연결 변경 저장'}
        </Button>
        {uncertain && attempt && (
          <Button variant="outline" disabled={pending || !canWrite} onClick={() => void send(true)}>
            같은 변경 다시 확인/재시도
          </Button>
        )}
        {(latest ?? error) && !uncertain && (
          <Button variant="outline" disabled={pending} onClick={() => void reconcile()}>
            최신 연결 다시 확인
          </Button>
        )}
      </div>
      <ConfirmDialog
        open={closeConfirmOpen}
        onOpenChange={setCloseConfirmOpen}
        target={uncertain ? '결과를 확인하지 못한 연결 변경' : '연결 변경 초안'}
        action="초안 폐기 후 닫기"
        consequence={
          uncertain
            ? '앞선 요청은 이미 저장되었을 수 있습니다. 화면을 닫으면 같은 요청을 다시 확인할 수 없습니다.'
            : '아직 저장하지 않은 연결 변경이 사라집니다.'
        }
        onConfirm={close}
      />
    </section>
  );
}
